// src/default-dict.ts
function defaultDict(defaultFactory) {
  return new Proxy(
    {},
    {
      get: (target, p) => {
        if (!(p in target)) {
          target[p] = defaultFactory();
        }
        return target[p];
      }
    }
  );
}

// src/lodash.ts
function range(start, end) {
  if (typeof end === "undefined") {
    return Array.from(Array(start), (_, i) => i);
  }
  return Array.from(Array(end - start), (_, i) => i + start);
}
function sortBy(ls, ...keyFns) {
  return sortByMap(ls, (x) => keyFns.map((f) => f(x)), arrayCompareFn);
}
function groupBy(ls, f) {
  const result = /* @__PURE__ */ new Map();
  for (const x of ls) {
    const y = f(x);
    if (!result.has(y)) {
      result.set(y, []);
    }
    result.get(y).push(x);
  }
  return result;
}
function mapKeys(map, f) {
  return new Map([...map].map(([k, v]) => [f(v, k), v]));
}
function mapValues(map, f) {
  return new Map([...map].map(([k, v]) => [k, f(v, k)]));
}
function mapGroupBy(ls, keyFn, valueFn) {
  return mapValues(groupBy(ls, keyFn), valueFn);
}
function pickBy(map, f) {
  return new Map([...map].filter(([k, v]) => f(v, k)));
}
function uniq(ls) {
  return Array.from(new Set(ls));
}
function uniqBy(ls, f) {
  const ys = /* @__PURE__ */ new Set();
  return ls.filter((x) => {
    const y = f(x);
    if (ys.has(y)) {
      return false;
    }
    ys.add(y);
    return true;
  });
}
function partition(ls, f) {
  const result = [[], []];
  for (const x of ls) {
    result[f(x) ? 0 : 1].push(x);
  }
  return result;
}
function zip(ls1, ls2) {
  return range(Math.min(ls1.length, ls2.length)).map((i) => [ls1[i], ls2[i]]);
}
function difference(ls1, ls2) {
  const exclude = new Set(ls2);
  return ls1.filter((e) => !exclude.has(e));
}
function capitalize(s) {
  return s.slice(0, 1).toUpperCase() + s.slice(1);
}
function isNil(value) {
  return value === null || typeof value === "undefined";
}
function isNotNil(value) {
  return !isNil(value);
}
function once(f) {
  let result;
  let called = false;
  function wrapper(...args) {
    if (!called) {
      result = f(...args);
      called = true;
    }
    return result;
  }
  return wrapper;
}
function objectPick(o, keys) {
  return objectPickBy(o, (_v, k) => keys.includes(k));
}
function objectOmit(o, keys) {
  return objectPickBy(o, (_v, k) => !keys.includes(k));
}
function objectPickBy(o, f) {
  return Object.fromEntries(
    Object.entries(o).filter(([k, v]) => f(v, k))
  );
}
function objectOmitBy(o, f) {
  return objectPickBy(o, (v, k) => !f(v, k));
}
function objectKeys(o) {
  return Object.keys(o);
}
function sortByMap(ls, mapFn, compareFn) {
  const mapped = ls.map(mapFn);
  function indexCompareFn(i, j) {
    return compareFn(mapped[i], mapped[j]);
  }
  return range(ls.length).sort(indexCompareFn).map((i) => ls[i]);
}
function anyCompareFn(x, y) {
  return x < y ? -1 : x > y ? 1 : 0;
}
function arrayCompareFn(xs, ys) {
  for (let i = 0; i < xs.length; i++) {
    const result = anyCompareFn(xs[i], ys[i]);
    if (result !== 0) {
      return result;
    }
  }
  return 0;
}

// src/default-map.ts
var DefaultMap = class extends Map {
  constructor(defaultFn, entries) {
    super(entries);
    this.defaultFn = defaultFn;
  }
  get(key) {
    if (!this.has(key)) {
      this.set(key, this.defaultFn(key));
    }
    return super.get(key);
  }
};
var UncheckedMap = class extends DefaultMap {
  constructor(entries) {
    super((key) => {
      throw new Error("UncheckedMap", { cause: { key } });
    }, entries);
  }
};
var HashKeyMap = class {
  constructor(keyFn = defaultKeyFn) {
    this.keyFn = keyFn;
  }
  map = /* @__PURE__ */ new Map();
  get(key) {
    return this.map.get(this.keyFn(key))?.[1];
  }
  set(key, value) {
    this.map.set(this.keyFn(key), [key, value]);
    return this;
  }
  delete(key) {
    return this.map.delete(this.keyFn(key));
  }
  has(key) {
    return this.map.has(this.keyFn(key));
  }
  clear() {
    this.map.clear();
  }
  entries() {
    return this.map.values();
  }
};
var HashKeyDefaultMap = class extends HashKeyMap {
  constructor(defaultFn, keyFn = defaultKeyFn) {
    super(keyFn);
    this.defaultFn = defaultFn;
  }
  get(key) {
    if (!this.has(key)) {
      this.set(key, this.defaultFn(key));
    }
    return super.get(key);
  }
};
function defaultKeyFn(v) {
  return JSON.stringify(sortBy(Object.entries(v), ([k]) => k));
}

// src/option.ts
function mapOption(x, f) {
  return typeof x === "undefined" ? x : f(x);
}

// src/result.ts
function Ok(value) {
  return { ok: true, value };
}
function Err(value) {
  return { ok: false, value };
}
function wrapError(getValue) {
  try {
    return Ok(getValue());
  } catch (e) {
    return Err(e);
  }
}
function okToOption(result) {
  return result.ok ? result.value : void 0;
}
async function wrapPromise(value) {
  try {
    return Ok(await value);
  } catch (e) {
    return Err(e);
  }
}

// src/tinyassert.ts
function tinyassert(value, message) {
  if (value) {
    return;
  }
  throw new TinyAssertionError(message, tinyassert);
}
var TinyAssertionError = class extends Error {
  constructor(message, stackStartFunction) {
    super(message);
    if ("captureStackTrace" in Error) {
      Error.captureStackTrace(this, stackStartFunction ?? TinyAssertionError);
    }
  }
};

// src/promise.ts
function newPromiseWithResolvers() {
  let resolve;
  let reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

// src/misc.ts
function assertUnreachable(value) {
  throw new Error("assertUnreachable", { cause: value });
}
var typedBoolean = Boolean;

// src/hash.ts
function hashInt32(x) {
  x ^= x >>> 16;
  x = Math.imul(x, 569420461);
  x ^= x >>> 15;
  x = Math.imul(x, 3545902487);
  x ^= x >>> 15;
  return x >>> 0;
}
var HashRng = class {
  state;
  constructor(seed) {
    this.state = hashInt32(seed | 1);
  }
  int32() {
    return this.state = hashInt32(this.state);
  }
  float() {
    return this.int32() / 2 ** 32;
  }
};
function hashString(input) {
  const xs = new Uint32Array(range(4).map((i) => hashInt32(i + 1)));
  for (const i of range(input.length)) {
    const c = input.codePointAt(i) ?? 0;
    xs[0] = hashInt32(xs[3] ^ c);
    xs[1] = hashInt32(xs[0] ^ c);
    xs[2] = hashInt32(xs[1] ^ c);
    xs[3] = hashInt32(xs[2] ^ c);
  }
  return Array.from(xs, (x) => x.toString(16).padStart(8, "0")).join("");
}
export {
  DefaultMap,
  Err,
  HashKeyDefaultMap,
  HashKeyMap,
  HashRng,
  Ok,
  TinyAssertionError,
  UncheckedMap,
  assertUnreachable,
  capitalize,
  defaultDict,
  difference,
  groupBy,
  hashInt32,
  hashString,
  isNil,
  isNotNil,
  mapGroupBy,
  mapKeys,
  mapOption,
  mapValues,
  newPromiseWithResolvers,
  objectKeys,
  objectOmit,
  objectOmitBy,
  objectPick,
  objectPickBy,
  okToOption,
  once,
  partition,
  pickBy,
  range,
  sortBy,
  tinyassert,
  typedBoolean,
  uniq,
  uniqBy,
  wrapError,
  wrapPromise,
  zip
};
